import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PlusCircle, MinusCircle, Search, Wallet as WalletIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { walletApi } from "@/features/wallet/api";
import { adminApi } from "@/features/admin/api";

/**
 * Super-Admin wallet management.
 * - Search customer → view balance + full ledger.
 * - Credit / Debit with a mandatory reason (>=3 chars).
 * - Also lets Super Admin set the WhatsApp recharge number.
 */
export default function AdminWallet() {
    const qc = useQueryClient();
    const [q, setQ] = useState("");
    const [selectedId, setSelectedId] = useState<string>("");
    const [action, setAction] = useState<"credit" | "debit">("credit");
    const [amount, setAmount] = useState<number>(0);
    const [reason, setReason] = useState("");
    const [whatsappNumber, setWhatsappNumber] = useState<string>("");

    const customers = useQuery({
        queryKey: ["admin", "customers", q],
        queryFn: () => adminApi.listCustomers({ search: q, page: 1, page_size: 25 }),
    });

    const walletQ = useQuery({
        queryKey: ["admin", "wallet", selectedId],
        queryFn: () => walletApi.adminGet(selectedId),
        enabled: !!selectedId,
    });

    const txnsQ = useQuery({
        queryKey: ["admin", "wallet-txns", selectedId],
        queryFn: () => walletApi.adminTransactions(selectedId, 1, 100),
        enabled: !!selectedId,
    });

    const mutate = useMutation({
        mutationFn: () =>
            action === "credit"
                ? walletApi.adminCredit(selectedId, amount, reason)
                : walletApi.adminDebit(selectedId, amount, reason),
        onSuccess: () => {
            toast.success(action === "credit" ? "Wallet credited" : "Wallet debited");
            qc.invalidateQueries({ queryKey: ["admin", "wallet", selectedId] });
            qc.invalidateQueries({ queryKey: ["admin", "wallet-txns", selectedId] });
            setAmount(0);
            setReason("");
        },
        onError: (e: unknown) =>
            toast.error(
                (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
                    "Action failed",
            ),
    });

    const saveWhatsapp = useMutation({
        mutationFn: () => walletApi.adminUpdateWhatsapp(whatsappNumber.trim()),
        onSuccess: () => toast.success("WhatsApp number saved"),
        onError: (e: unknown) =>
            toast.error(
                (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
                    "Failed to save",
            ),
    });

    return (
        <div data-testid="admin-wallet-page" className="space-y-6">
            <header className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand text-white">
                    <WalletIcon className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold">TSGo Wallets</h1>
                    <p className="text-sm text-muted-foreground">
                        Credit or debit customer wallets. Every action requires a reason and is audit-logged.
                    </p>
                </div>
            </header>

            {/* WhatsApp settings */}
            <section className="rounded-2xl border border-border/60 bg-white p-5">
                <p className="text-sm font-bold">Wallet recharge WhatsApp number</p>
                <p className="mt-1 text-xs text-muted-foreground">
                    Customers tap this number from their /wallet page to request top-ups.
                </p>
                <div className="mt-3 flex gap-2">
                    <Input
                        data-testid="admin-wallet-whatsapp-number"
                        placeholder="+91 XXXXX XXXXX"
                        value={whatsappNumber}
                        onChange={(e) => setWhatsappNumber(e.target.value)}
                    />
                    <Button
                        onClick={() => saveWhatsapp.mutate()}
                        disabled={!whatsappNumber.trim() || saveWhatsapp.isPending}
                        className="bg-brand text-white"
                        data-testid="admin-wallet-save-whatsapp"
                    >
                        Save
                    </Button>
                </div>
            </section>

            {/* Customer picker */}
            <section className="grid gap-6 md:grid-cols-3">
                <div className="md:col-span-1 rounded-2xl border border-border/60 bg-white p-4">
                    <Label htmlFor="q">Search customer</Label>
                    <div className="relative mt-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="q"
                            data-testid="admin-wallet-search"
                            className="pl-9"
                            placeholder="Name, email, phone"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <ul className="mt-3 max-h-96 space-y-1 overflow-y-auto">
                        {customers.data?.items.map((c) => (
                            <li key={c.id}>
                                <button
                                    onClick={() => setSelectedId(c.id)}
                                    data-testid={`admin-wallet-customer-${c.email}`}
                                    className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5 ${
                                        selectedId === c.id ? "bg-brand/10 font-semibold" : ""
                                    }`}
                                >
                                    <p className="truncate">{c.full_name || c.email}</p>
                                    <p className="text-xs text-muted-foreground">{c.email}</p>
                                </button>
                            </li>
                        ))}
                        {!customers.data?.items.length && (
                            <p className="p-3 text-xs text-muted-foreground">No customers found.</p>
                        )}
                    </ul>
                </div>

                {/* Wallet detail */}
                <div className="md:col-span-2 space-y-4">
                    {!selectedId ? (
                        <div className="rounded-2xl border border-dashed border-border/60 bg-white p-10 text-center text-sm text-muted-foreground">
                            Select a customer to view their wallet.
                        </div>
                    ) : (
                        <>
                            <div className="rounded-2xl bg-ink p-5 text-white" data-testid="admin-wallet-balance-card">
                                <p className="text-xs uppercase tracking-widest text-white/60">
                                    {walletQ.data?.customer.full_name || walletQ.data?.customer.email} · balance
                                </p>
                                <p className="mt-2 text-3xl font-extrabold" data-testid="admin-wallet-balance">
                                    ₹{(walletQ.data?.wallet.balance ?? 0).toLocaleString("en-IN")}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-border/60 bg-white p-5">
                                <p className="text-sm font-bold">Credit or debit</p>
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={() => setAction("credit")}
                                        data-testid="admin-wallet-tab-credit"
                                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${action === "credit" ? "bg-emerald-600 text-white" : "bg-white text-ink border border-border"}`}
                                    >
                                        <PlusCircle className="h-3 w-3" /> Credit
                                    </button>
                                    <button
                                        onClick={() => setAction("debit")}
                                        data-testid="admin-wallet-tab-debit"
                                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${action === "debit" ? "bg-rose-600 text-white" : "bg-white text-ink border border-border"}`}
                                    >
                                        <MinusCircle className="h-3 w-3" /> Debit
                                    </button>
                                </div>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="w-amt">Amount (₹)</Label>
                                        <Input
                                            id="w-amt"
                                            type="number"
                                            min="1"
                                            data-testid="admin-wallet-amount"
                                            value={amount || ""}
                                            onChange={(e) => setAmount(Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="w-reason">Reason (required)</Label>
                                        <Input
                                            id="w-reason"
                                            data-testid="admin-wallet-reason"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            minLength={3}
                                        />
                                    </div>
                                </div>
                                <Button
                                    className="mt-4 bg-brand text-white"
                                    data-testid="admin-wallet-submit"
                                    disabled={mutate.isPending || amount <= 0 || reason.trim().length < 3}
                                    onClick={() => mutate.mutate()}
                                >
                                    {action === "credit" ? "Credit wallet" : "Debit wallet"}
                                </Button>
                            </div>

                            {/* Ledger */}
                            <div className="rounded-2xl border border-border/60 bg-white">
                                <div className="border-b border-border/60 px-5 py-3 text-sm font-bold">
                                    Transaction history
                                </div>
                                <div className="max-h-[420px] overflow-y-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="sticky top-0 bg-black/5 text-xs uppercase tracking-widest text-muted-foreground">
                                            <tr>
                                                <th className="px-4 py-2">When</th>
                                                <th className="px-4 py-2">Type</th>
                                                <th className="px-4 py-2">Amount</th>
                                                <th className="px-4 py-2">Reason</th>
                                                <th className="px-4 py-2">Ref</th>
                                                <th className="px-4 py-2">Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {txnsQ.data?.items.map((t) => (
                                                <tr key={t.id} className="border-t border-border/40" data-testid={`admin-wallet-txn-${t.reference}`}>
                                                    <td className="px-4 py-2 text-xs">{new Date(t.at).toLocaleString()}</td>
                                                    <td className={`px-4 py-2 text-xs font-bold ${t.kind === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                                                        {t.kind}
                                                    </td>
                                                    <td className="px-4 py-2 font-bold">
                                                        {t.kind === "credit" ? "+" : "−"}₹{t.amount.toLocaleString("en-IN")}
                                                    </td>
                                                    <td className="px-4 py-2">{t.reason}</td>
                                                    <td className="px-4 py-2 font-mono text-xs">{t.reference}</td>
                                                    <td className="px-4 py-2 font-mono text-xs">
                                                        ₹{t.balance_after.toLocaleString("en-IN")}
                                                    </td>
                                                </tr>
                                            ))}
                                            {!txnsQ.data?.items.length && (
                                                <tr>
                                                    <td colSpan={6} className="p-5 text-center text-muted-foreground">
                                                        No wallet activity yet.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
